import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  CalendarToday as CalendarIcon,
  Bookmark as BookmarkIcon,
  Inbox as InboxIcon,
} from '@mui/icons-material';
import { fetchApiData } from '../../../store/slicers/apiSlicer.js';

const BriefsVisitor = () => {
  const dispatch = useDispatch();
  const [briefs, setBriefs] = useState([]);

  const { data, isLoading } = useSelector((state) => ({
    data: state.apiData?.data?.briefsRecent,
    isLoading: state.apiData?.loading?.briefsRecent
  }));

  useEffect(() => {
    dispatch(fetchApiData({ url: 'brief/recent?limit=5', itemKey: 'briefsRecent' }));
  }, [dispatch]);

  useEffect(() => {
    if (data?.data) {
      setBriefs(data.data);
    }
  }, [data]);

  if (isLoading) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, pb: 2, borderBottom: '3px solid #3b82f6' }}>
          <CampaignIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            Brèves d'Actualité
          </Typography>
        </Stack>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#3b82f6' }} />
          <Typography sx={{ mt: 2, color: '#94a3b8' }}>
            Chargement des brèves...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!briefs || briefs.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, pb: 2, borderBottom: '3px solid #3b82f6' }}>
          <CampaignIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
          <Typography variant="h5" fontWeight={700} color="#1e293b">
            Brèves d'Actualité
          </Typography>
        </Stack>
        <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
          <InboxIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
          <Typography>
            Aucune brève disponible pour le moment
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
      <Stack 
        direction="row" 
        spacing={2} 
        alignItems="center" 
        sx={{ mb: 3, pb: 2, borderBottom: '3px solid #3b82f6' }}
      >
        <CampaignIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
        <Typography variant="h5" fontWeight={700} color="#1e293b" sx={{ flex: 1 }}>
          Brèves d'Actualité
        </Typography>
        <Chip 
          label={briefs.length} 
          sx={{ 
            bgcolor: '#3b82f6', 
            color: 'white',
            fontWeight: 600 
          }} 
        />
      </Stack>

      <Stack spacing={2}>
        {briefs.map((brief, index) => (
          <Card
            key={brief.id || index}
            sx={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderLeft: '4px solid #3b82f6',
              transition: 'all 0.3s ease',
              position: 'relative',
              '&:hover': {
                transform: 'translateX(8px)',
                boxShadow: '0 8px 16px rgba(59, 130, 246, 0.15)',
              },
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: '#3b82f6',
                    borderRadius: '50%',
                    mt: 1,
                    flexShrink: 0,
                    boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.2)',
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="h6" 
                    fontWeight={600} 
                    color="#1e293b"
                    sx={{ mb: 1 }}
                  >
                    {brief.title}
                  </Typography>

                  <Typography 
                    variant="body2" 
                    color="#475569"
                    sx={{ mb: 2, lineHeight: 1.6 }}
                  >
                    {brief.content}
                  </Typography>

                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={1} 
                    alignItems="flex-start"
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CalendarIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                      <Typography variant="caption" color="#64748b">
                        {new Date(brief.published_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </Typography>
                    </Stack>

                    {brief.source && (
                      <Chip
                        icon={<BookmarkIcon sx={{ fontSize: 14 }} />}
                        label={brief.source}
                        size="small"
                        sx={{
                          bgcolor: 'white',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Paper>
  );
};

export default BriefsVisitor;